import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { EmployeeModal, EmployeeDetailsModal } from './payroll-modals'
import { formatCurrency } from '@/lib/utils'
import { TranslatedText } from '@/components/translated-text'
import { useTranslation } from '@/hooks/useTranslation'
import {
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Users,
  Phone,
  Mail,
  MapPin,
  Calendar,
  DollarSign,
  Building2,
  User,
  Trash2,
  Copy,
  Save,
  X,
  Check,
  AlertCircle,
  Clock,
  Shield,
  Star,
  Target,
  TrendingUp,
  Activity,
  BarChart3,
  PieChart,
  Calculator,
  FileText,
  Download,
  Upload,
  RefreshCw,
  Settings,
  Bell,
  ArrowRightLeft,
  Zap,
  Brain,
  Thermometer,
  UserCheck,
  HelpCircle,
  Home,
  ShoppingCart,
  ShoppingBag,
  Banknote,
  Receipt,
  Wallet,
  Package,
  BookOpen,
  MessageSquare,
  FolderOpen,
  Building,
  Settings as SettingsIcon,
  HelpCircle as HelpCircleIcon,
  Brain as BrainIcon,
  Zap as ZapIcon,
  BarChart3 as BarChart3Icon,
  Activity as ActivityIcon,
  Calculator as CalculatorIcon,
  Loader2,
  Receipt as ReceiptIcon
} from "lucide-react"

interface Employee {
  id: string
  employeeNumber: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  department?: string
  position?: string
  employmentStatus: string
  employmentType: string
  payRate: number
  payRateType: string
  basicSalary?: number
  hireDate: string
  manager?: {
    firstName: string
    lastName: string
  }
  _count?: {
    subordinates: number
  }
}

interface EmployeeManagementProps {
  employees: Employee[]
  totalEmployees: number
  onEditEmployee: (employee: Employee) => void
  onCreateEmployee: (employeeData: any) => void
  onOpenCreateModal: () => void
  searchTerm: string
  setSearchTerm: (term: string) => void
  filterDepartment: string
  setFilterDepartment: (dept: string) => void
  filterStatus: string
  setFilterStatus: (status: string) => void
}

export function EmployeeManagement({
  employees,
  totalEmployees,
  onEditEmployee,
  onCreateEmployee,
  onOpenCreateModal,
  searchTerm,
  setSearchTerm,
  filterDepartment,
  setFilterDepartment,
  filterStatus,
  setFilterStatus
}: EmployeeManagementProps) {
  const { tSync } = useTranslation()
  // Removed internal modal state - now handled by parent component
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [selectedEmployeeForDetails, setSelectedEmployeeForDetails] = useState<Employee | null>(null)

  const handleEditEmployee = (employee: Employee) => {
    onEditEmployee(employee)
  }

  const handleViewEmployee = (employee: Employee) => {
    setSelectedEmployeeForDetails(employee)
    setIsDetailsModalOpen(true)
  }

  const handleCreateEmployee = () => {
    onOpenCreateModal()
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: tSync('Active') },
      terminated: { color: 'bg-red-100 text-red-800', label: tSync('Terminated') },
      on_leave: { color: 'bg-yellow-100 text-yellow-800', label: tSync('On Leave') },
      suspended: { color: 'bg-gray-100 text-gray-800', label: tSync('Suspended') }
    }
    const config = statusConfig[status] || statusConfig.active
    return <Badge className={config.color}>{config.label}</Badge>
  }

  const getEmploymentTypeBadge = (type: string) => {
    const typeConfig = {
      full_time: { color: 'bg-blue-100 text-blue-800', label: tSync('Full Time') },
      part_time: { color: 'bg-purple-100 text-purple-800', label: tSync('Part Time') },
      contractor: { color: 'bg-orange-100 text-orange-800', label: tSync('Contractor') },
      intern: { color: 'bg-pink-100 text-pink-800', label: tSync('Intern') }
    }
    const config = typeConfig[type] || typeConfig.full_time
    return <Badge className={config.color}>{config.label}</Badge>
  }

  return (
    <div className="space-y-4">
      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span><TranslatedText text="Employee Management" /></span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={tSync("Search employees...")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterDepartment} onValueChange={setFilterDepartment}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder={tSync("All Departments")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all"><TranslatedText text="All Departments" /></SelectItem>
                <SelectItem value="Engineering"><TranslatedText text="Engineering" /></SelectItem>
                <SelectItem value="Sales"><TranslatedText text="Sales" /></SelectItem>
                <SelectItem value="Marketing"><TranslatedText text="Marketing" /></SelectItem>
                <SelectItem value="HR"><TranslatedText text="Human Resources" /></SelectItem>
                <SelectItem value="Finance"><TranslatedText text="Finance" /></SelectItem>
                <SelectItem value="Operations"><TranslatedText text="Operations" /></SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder={tSync("All Status")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all"><TranslatedText text="All Status" /></SelectItem>
                <SelectItem value="active"><TranslatedText text="Active" /></SelectItem>
                <SelectItem value="terminated"><TranslatedText text="Terminated" /></SelectItem>
                <SelectItem value="on_leave"><TranslatedText text="On Leave" /></SelectItem>
                <SelectItem value="suspended"><TranslatedText text="Suspended" /></SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleCreateEmployee} className="flex items-center space-x-2">
              <Plus className="h-4 w-4" />
              <span><TranslatedText text="Add Employee" /></span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Employees Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {employees.map((employee) => (
          <Card key={employee.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">
                      {employee.firstName} {employee.lastName}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      #{employee.employeeNumber}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleViewEmployee(employee)}
                    title={tSync("View Details")}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditEmployee(employee)}
                    title={tSync("Edit Employee")}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{employee.email}</span>
              </div>
              {employee.phone && (
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{employee.phone}</span>
                </div>
              )}
              <div className="flex items-center space-x-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{employee.department || <TranslatedText text="No Department" />}</span>
              </div>
              <div className="flex items-center space-x-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <span className="text-lg font-bold text-green-600">
                  {(() => {
                    const currency = employee.salaryCurrency
                    if (employee.payRateType === 'salary') {
                      return `${formatCurrency(employee.basicSalary || 0, currency)}/month`
                    } else if (employee.payRateType === 'hourly') {
                      return `${formatCurrency(employee.payRate || 0, currency)}/hour`
                    } else if (employee.payRateType === 'commission') {
                      return `Commission: ${employee.payRate?.toLocaleString() || '0'}%`
                    } else {
                      return `${formatCurrency(employee.payRate || 0, currency)}/${employee.payRateType}`
                    }
                  })()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                {getStatusBadge(employee.employmentStatus)}
                {getEmploymentTypeBadge(employee.employmentType)}
              </div>
              {employee.manager && (
                <div className="flex items-center space-x-2">
                  <UserCheck className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    <TranslatedText text="Manager" />: {employee.manager.firstName} {employee.manager.lastName}
                  </span>
                </div>
              )}
              {employee._count?.subordinates > 0 && (
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {employee._count.subordinates} <TranslatedText text="direct reports" />
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>


      {employees.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2"><TranslatedText text="No employees found" /></h3>
            <p className="text-muted-foreground text-center mb-4">
              {searchTerm || filterDepartment || filterStatus
                ? <TranslatedText text="Try adjusting your search criteria" />
                : <TranslatedText text="Get started by adding your first employee" />
              }
            </p>
            <Button onClick={handleCreateEmployee}>
              <Plus className="h-4 w-4 mr-2" />
              <TranslatedText text="Add Employee" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Modals are now handled by the parent component (payroll.tsx) */}
      
      {/* Employee Details Modal */}
      <EmployeeDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false)
          setSelectedEmployeeForDetails(null)
        }}
        employee={selectedEmployeeForDetails}
      />
    </div>
  )
}

// EmployeeFormModal component removed - now handled by parent component
